import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { 
  MessageCircle, HelpCircle, Users, Plus, Eye, Clock, CheckCircle,
  Send, ThumbsUp, ArrowLeft
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  diskussion: { label: "Diskussion", icon: MessageCircle, color: "bg-blue-500/10 text-blue-500" },
  frage: { label: "Frage", icon: HelpCircle, color: "bg-yellow-500/10 text-yellow-500" },
  lerngruppe: { label: "Lerngruppe", icon: Users, color: "bg-green-500/10 text-green-500" }
};

const Community = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Dialogs
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [newGroupDialog, setNewGroupDialog] = useState(false);
  const [postDetailDialog, setPostDetailDialog] = useState(false);
  
  // Form
  const [postForm, setPostForm] = useState({ title: "", content: "", category: "diskussion" });
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [replyContent, setReplyContent] = useState("");

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === "lerngruppen") {
        const res = await axios.get(`${API}/community/groups`);
        setGroups(res.data);
      } else {
        const category = activeTab === "all" ? null : activeTab;
        const res = await axios.get(`${API}/community/posts`, { params: { category } });
        setPosts(res.data);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.content) {
      toast.error("Bitte Titel und Inhalt eingeben");
      return;
    }
    try {
      await axios.post(`${API}/community/posts`, null, {
        params: { title: postForm.title, content: postForm.content, category: postForm.category }
      });
      toast.success("Beitrag erstellt");
      setNewPostDialog(false);
      setPostForm({ title: "", content: "", category: "diskussion" });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name) {
      toast.error("Bitte Namen eingeben");
      return;
    }
    try {
      await axios.post(`${API}/community/groups`, null, {
        params: { name: groupForm.name, description: groupForm.description }
      });
      toast.success("Lerngruppe erstellt");
      setNewGroupDialog(false);
      setGroupForm({ name: "", description: "" });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const openPostDetail = async (postId) => {
    try {
      const res = await axios.get(`${API}/community/posts/${postId}`);
      setSelectedPost(res.data);
      setPostDetailDialog(true);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedPost) return;
    try {
      await axios.post(`${API}/community/posts/${selectedPost.id}/reply`, null, {
        params: { content: replyContent }
      });
      toast.success("Antwort gesendet");
      setReplyContent("");
      // Reload post
      const res = await axios.get(`${API}/community/posts/${selectedPost.id}`);
      setSelectedPost(res.data);
    } catch (error) {
      toast.error("Fehler beim Senden");
    }
  };

  const handleMarkAnswer = async (replyId) => {
    try {
      await axios.post(`${API}/community/posts/${selectedPost.id}/mark-answered`, null, {
        params: { reply_id: replyId }
      });
      toast.success("Antwort markiert");
      const res = await axios.get(`${API}/community/posts/${selectedPost.id}`);
      setSelectedPost(res.data);
      loadData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/community/groups/${groupId}/join`);
      toast.success("Gruppe beigetreten");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/community/groups/${groupId}/leave`);
      toast.success("Gruppe verlassen");
      loadData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <ParticipantLayout title="Community">
      <div className="space-y-6" data-testid="community-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <p className="text-muted-foreground">
              Tauschen Sie sich mit anderen Teilnehmern aus, stellen Sie Fragen und lernen Sie gemeinsam.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setNewPostDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Neuer Beitrag
            </Button>
            <Button variant="outline" onClick={() => setNewGroupDialog(true)}>
              <Users className="h-4 w-4 mr-2" />
              Lerngruppe erstellen
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="all">Alle</TabsTrigger>
            <TabsTrigger value="diskussion">Diskussionen</TabsTrigger>
            <TabsTrigger value="frage">Fragen</TabsTrigger>
            <TabsTrigger value="lerngruppen">Lerngruppen</TabsTrigger>
          </TabsList>

          {/* Posts View */}
          {activeTab !== "lerngruppen" && (
            <TabsContent value={activeTab} className="mt-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : posts.length === 0 ? (
                <Card className="bg-card border-border">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Beiträge</p>
                    <Button className="mt-4" onClick={() => setNewPostDialog(true)}>
                      Ersten Beitrag erstellen
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {posts.map(post => {
                    const config = CATEGORY_CONFIG[post.category];
                    const Icon = config?.icon || MessageCircle;
                    return (
                      <Card 
                        key={post.id} 
                        className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={() => openPostDetail(post.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg ${config?.color || "bg-muted"} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium truncate">{post.title}</h3>
                                {post.is_answered && (
                                  <Badge className="bg-green-500/10 text-green-500 border border-green-500/30">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Beantwortet
                                  </Badge>
                                )}
                                {post.is_pinned && (
                                  <Badge variant="secondary">Angepinnt</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span>{post.author?.name || "Unbekannt"}</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDate(post.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" />
                                  {post.view_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageCircle className="h-3 w-3" />
                                  {post.reply_count} Antworten
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* Learning Groups View */}
          <TabsContent value="lerngruppen" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : groups.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Lerngruppen</p>
                  <Button className="mt-4" onClick={() => setNewGroupDialog(true)}>
                    Erste Lerngruppe erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups.map(group => (
                  <Card key={group.id} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-base">{group.name}</CardTitle>
                      {group.description && (
                        <CardDescription className="line-clamp-2">{group.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {group.member_count} / {group.max_members || 20} Mitglieder
                        </span>
                        {group.is_member ? (
                          <Button variant="outline" size="sm" onClick={() => handleLeaveGroup(group.id)}>
                            Verlassen
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => handleJoinGroup(group.id)}>
                            Beitreten
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New Post Dialog */}
        <Dialog open={newPostDialog} onOpenChange={setNewPostDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neuer Beitrag</DialogTitle>
              <DialogDescription>Teilen Sie Ihre Gedanken oder stellen Sie eine Frage.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Kategorie</Label>
                <Select value={postForm.category} onValueChange={(v) => setPostForm({ ...postForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diskussion">Diskussion</SelectItem>
                    <SelectItem value="frage">Frage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  placeholder="Worum geht es?"
                />
              </div>
              <div className="space-y-2">
                <Label>Inhalt</Label>
                <Textarea
                  value={postForm.content}
                  onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                  placeholder="Beschreiben Sie Ihr Anliegen..."
                  rows={5}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNewPostDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreatePost}>Veröffentlichen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Group Dialog */}
        <Dialog open={newGroupDialog} onOpenChange={setNewGroupDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neue Lerngruppe</DialogTitle>
              <DialogDescription>Erstellen Sie eine Gruppe zum gemeinsamen Lernen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="z.B. NTG Prüfungsvorbereitung"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  placeholder="Was ist das Ziel der Gruppe?"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNewGroupDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreateGroup}>Erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Post Detail Dialog */}
        <Dialog open={postDetailDialog} onOpenChange={setPostDetailDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedPost && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <Badge className={CATEGORY_CONFIG[selectedPost.category]?.color}>
                      {CATEGORY_CONFIG[selectedPost.category]?.label}
                    </Badge>
                    {selectedPost.is_answered && (
                      <Badge className="bg-green-500/10 text-green-500 border border-green-500/30">
                        Beantwortet
                      </Badge>
                    )}
                  </div>
                  <DialogTitle className="text-xl">{selectedPost.title}</DialogTitle>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{selectedPost.author?.name}</span>
                    <span>{formatDate(selectedPost.created_at)}</span>
                    <span>{selectedPost.view_count} Aufrufe</span>
                  </div>
                </DialogHeader>
                
                <div className="py-4 border-b border-border">
                  <p className="whitespace-pre-wrap">{selectedPost.content}</p>
                </div>

                {/* Replies */}
                <div className="space-y-4 py-4">
                  <h4 className="font-medium">{selectedPost.replies?.length || 0} Antworten</h4>
                  
                  {selectedPost.replies?.map(reply => (
                    <div key={reply.id} className={`p-3 rounded-lg ${reply.is_best_answer ? "bg-green-500/10 border border-green-500/30" : "bg-secondary/30"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{reply.author?.name}</span>
                          {reply.is_best_answer && (
                            <Badge className="bg-green-500 text-white text-xs">Beste Antwort</Badge>
                          )}
                          {reply.author?.role === "dozent" && (
                            <Badge variant="outline" className="text-xs">Dozent</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(reply.created_at)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
                      {selectedPost.category === "frage" && !selectedPost.is_answered && selectedPost.author_id === user?.id && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-2 text-xs"
                          onClick={() => handleMarkAnswer(reply.id)}
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          Als beste Antwort markieren
                        </Button>
                      )}
                    </div>
                  ))}

                  {/* Reply Input */}
                  <div className="flex gap-2">
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Ihre Antwort..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleReply} disabled={!replyContent.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ParticipantLayout>
  );
};

export default Community;
